import { AccountInfo, IPublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { HttpStatusCode } from 'axios';

import { getMsalClient } from '../../uip';
import { getAccount } from '../../uip/utilities';
import { getMavenoidApiConfig } from '../config/actions';
import { IMavenoidApiClient, JWTForMavenoid } from '../models';

class MavenoidApiClient implements IMavenoidApiClient {
  private readonly msalClient: IPublicClientApplication;

  constructor() {
    this.msalClient = getMsalClient();
  }

  public get account(): AccountInfo | undefined {
    return getAccount();
  }

  public getJWTForMavenoid = async (): Promise<JWTForMavenoid> => {
    const { scopes } = getMavenoidApiConfig();
    if (this.account) {
      return await this.acquireToken({
        account: this.account,
        forceRefresh: false,
        scopes,
      });
    } else {
      return {
        errorCode: HttpStatusCode.Unauthorized,
        errorMessage: 'User Not Logged In',
        JWT: null,
      };
    }
  };

  private async acquireToken(request: SilentRequest): Promise<JWTForMavenoid> {
    try {
      const { accessToken } = await this.msalClient.acquireTokenSilent(request);
      return { JWT: accessToken };
    } catch (error: unknown) {
      console.warn(error);
      return { errorMessage: error as string, errorCode: HttpStatusCode.Forbidden, JWT: null };
    }
  }
}

/** Singleton */
let mavenoidApiClient: IMavenoidApiClient;

type WindowWithMavenoidApiClient = Window &
  typeof globalThis & {
    MavenoidApiClient: IMavenoidApiClient;
  };

export const initializeMavenoidApiClient = (): IMavenoidApiClient => {
  // Check whether it's browser environment or not.
  if (typeof window === 'undefined') {
    return mavenoidApiClient || (mavenoidApiClient = new MavenoidApiClient());
  }

  const existingClient: IMavenoidApiClient | undefined = (window as WindowWithMavenoidApiClient)
    .MavenoidApiClient;
  if (!existingClient) {
    (window as WindowWithMavenoidApiClient).MavenoidApiClient =
      mavenoidApiClient || (mavenoidApiClient = new MavenoidApiClient());
  }

  return (window as WindowWithMavenoidApiClient).MavenoidApiClient;
};
